import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { complectationOptions } from '@/repositories/complectation/complectationOptions.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    Complectation,
    ComplectationsRequestFilter,
    ComplectationCreateFormErrors,
    ComplectationCreateFormErrorsSchema, ComplectationOptionItemList, ComplectationOptionsList,
    ComplectationSchema,
    ComplectationsPaginator,
    ComplectationsPaginatorSchema, ComplectationsSchema,
    ComplectationUpdateFormErrors,
    ComplectationUpdateFormErrorsSchema, ComplectationCreateForm, ComplectationUpdateForm,
} from '@/types/complectation.type.ts';
import { convertFlatMapToNestedMap } from '@/utils/collection-helpers.ts';

class ComplectationRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getComplectation(id: number): Promise<Complectation>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getComplectation', requestParams)
                .then(({ data }: any) => {
                    const parsedComplectation = ComplectationSchema.parse(data);
                    const complectation: Complectation = parsedComplectation as unknown as Complectation;

                    resolve(complectation);
                });
        });
    }

    public getComplectations(complectationsRequestFilter: ComplectationsRequestFilter, oAbortController?: AbortController): Promise<ComplectationsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: complectationsRequestFilter,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getComplectations', requestParams, null, oAbortController)
                .then(({ data }: any) => {
                    const parsedComplectationsPaginator = ComplectationsPaginatorSchema.parse(data);
                    const complectationsPaginator: ComplectationsPaginator = parsedComplectationsPaginator as unknown as ComplectationsPaginator;

                    resolve(complectationsPaginator);
                });
        });
    }

    public createComplectation(complectationCreateForm: ComplectationCreateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: complectationCreateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('createComplectation', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedCreateErrors = ComplectationCreateFormErrorsSchema.parse(nestedErrors);
                        const createErrors: ComplectationCreateFormErrors = parsedCreateErrors as unknown as ComplectationCreateFormErrors;

                        reject(createErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public updateComplectation(id: number, complectationUpdateForm: ComplectationUpdateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: complectationUpdateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateComplectation', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedUpdateErrors = ComplectationUpdateFormErrorsSchema.parse(nestedErrors);
                        const updateErrors: ComplectationUpdateFormErrors = parsedUpdateErrors as unknown as ComplectationUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public deleteComplectation(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('deleteComplectation', requestParams)
                .then(() => resolve());
        });
    }

    public getComplectationOptions(): Promise<ComplectationOptionsList>
    {
        return new Promise((resolve) => {
            const options: ComplectationOptionsList = {};

            for (let symbolic_code in complectationOptions) {
                options[symbolic_code] = {
                    name: complectationOptions[symbolic_code].name,
                    values: { ...complectationOptions[symbolic_code].values },
                };
            }

            resolve(options);
        });
    }

    public getComplectationOption(symbolic_code: string): Promise<ComplectationOptionItemList>
    {
        return new Promise((resolve) => {
            if (!(symbolic_code in complectationOptions)) {
                throw new Error();
            }

            const option = {
                name: complectationOptions[symbolic_code].name,
                values: { ...complectationOptions[symbolic_code].values },
            };

            resolve(option);
        });
    }

    public getComplectationOptionValues(symbolic_code: string): Promise<Record<string, string>>
    {
        return new Promise((resolve) => {
            if (!(symbolic_code in complectationOptions)) {
                throw new Error();
            }

            const values = { ...complectationOptions[symbolic_code].values };

            resolve(values);
        });
    }

    public getComplectationsByModelId(id: number): Promise<Complectation[]>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getComplectationsByModelId', requestParams)
                .then(({ data }: any) => {
                    const parsedComplectations = ComplectationsSchema.parse(data);
                    const complectations: Complectation[] = parsedComplectations as unknown as Complectation[];

                    resolve(complectations);
                });
        });
    }
}

export default ComplectationRepository;
